// Panel de Usuarios (Administración)
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useHistory } from "react-router-dom";
import UsuarioModal from "./UsuarioModal";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

const ROL_LABEL = { 1: "Administrador", 2: "Usuario", 3: "Proveedor" };

const S = {
  page: { minHeight: "100vh", background: "#f7f4f6", padding: "32px 24px 48px", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  inner: { maxWidth: 1100, margin: "0 auto" },
  title: { fontSize: 22, fontWeight: 700, color: "#222", marginBottom: 4 },
  sub: { fontSize: 13, color: "#888", marginBottom: 24 },
  card: { background: "#fff", borderRadius: 14, border: "1.5px solid #f0e6ec", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { background: "#9f0059", color: "#fff", fontWeight: 700, padding: "11px 14px", textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" },
  td: { padding: "10px 14px", borderBottom: "1px solid #f0e6ec", color: "#333", verticalAlign: "middle" },
  select: { padding: "6px 10px", border: "1.5px solid #e0d6dc", borderRadius: 7, fontSize: 13, background: "#fff", color: "#222", outline: "none", cursor: "pointer" },
  check: { width: 17, height: 17, accentColor: "#9f0059", cursor: "pointer" },
  empty: { textAlign: "center", padding: "40px", color: "#aaa", fontSize: 14 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  btnNew: { padding: "9px 18px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "3px 7px", borderRadius: 5, fontWeight: 600 },
};

// Toggle estilo switch
const Toggle = ({ on, onChange }) => (
  <button
    onClick={onChange}
    style={{
      width: 40, height: 22, borderRadius: 22, border: "none", cursor: "pointer", position: "relative",
      background: on ? "#16a34a" : "#cbd5e1", transition: "background 0.15s", padding: 0,
    }}
    title={on ? "Activo" : "Inactivo"}
  >
    <span style={{
      position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: "50%",
      background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
    }} />
  </button>
);

const UsersAdmin = () => {
  const history = useHistory();
  const [usuarios, setUsuarios] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | {} (nuevo) | usuario (editar)

  const traer = () => {
    setLoading(true);
    axios.post(`${BASE}/buscar_usuarios.php`)
      .then((r) => { if (Array.isArray(r.data)) setUsuarios(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { traer(); }, []);

  // Guarda un usuario con los valores nuevos (optimista) y persiste
  const actualizar = async (u, cambios) => {
    const nuevo = { ...u, ...cambios };
    setUsuarios((prev) => prev.map((x) => (x.id === u.id ? nuevo : x)));
    try {
      const r = await axios.post(`${BASE}/usuarios_actualizar.php`, new URLSearchParams({
        id:        u.id,
        tipo:      cambios.tipo_id      ?? u.tipo_id ?? 2,
        comercial: (cambios.comercial   ?? u.comercial) ? 1 : 0,
        activo:    (cambios.activo       ?? u.activo)    ? 1 : 0,
      }));
      if (!r.data?.success) { toast.error(r.data?.mensaje || "Error al guardar"); traer(); }
    } catch { toast.error("Error de conexión"); traer(); }
  };

  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.nombre || u.mail}? Dejará de tener acceso y no aparecerá más en el listado.`)) return;
    try {
      const r = await axios.post(`${BASE}/usuarios_eliminar.php`, new URLSearchParams({ id: u.id }));
      if (r.data?.success) { toast.success("Usuario eliminado"); traer(); }
      else toast.error(r.data?.mensaje || "Error");
    } catch { toast.error("Error de conexión"); }
  };

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <div style={S.headerRow}>
          <div>
            <p style={S.title}>Usuarios</p>
            <p style={{ ...S.sub, marginBottom: 0 }}>Gestioná datos, rol y acceso de cada usuario. Los toggles se guardan al instante. (Proveedor = solo lectura, próximamente.)</p>
          </div>
          <button style={S.btnNew} onClick={() => setModal({})}>+ Nuevo usuario</button>
        </div>

        <div style={S.card}>
          {loading ? (
            <p style={S.empty}>Cargando…</p>
          ) : usuarios.length === 0 ? (
            <p style={S.empty}>No hay usuarios.</p>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Nombre</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Empresa</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Rol</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Comercial</th>
                  <th style={{ ...S.th, textAlign: "center" }}>Activo</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u, i) => {
                  const rol = Number(u.tipo_id) || 2;
                  const esComercial = Number(u.comercial) === 1;
                  const activo = Number(u.activo) === 1;
                  return (
                    <tr key={u.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{u.nombre || "—"}</td>
                      <td style={S.td}>{u.mail || "—"}</td>
                      <td style={{ ...S.td, color: "#666" }}>{u.empresa || "—"}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <select
                          style={S.select}
                          value={rol}
                          onChange={(e) => actualizar(u, { tipo_id: Number(e.target.value) })}
                        >
                          <option value={1}>Administrador</option>
                          <option value={2}>Usuario</option>
                          <option value={3}>Proveedor</option>
                        </select>
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          style={S.check}
                          checked={esComercial}
                          onChange={(e) => actualizar(u, { comercial: e.target.checked ? 1 : 0 })}
                        />
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <Toggle on={activo} onChange={() => actualizar(u, { activo: activo ? 0 : 1 })} />
                      </td>
                      <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button style={{ ...S.iconBtn, color: "#555" }} onClick={() => setModal(u)} title="Editar">✏️</button>
                        <button style={{ ...S.iconBtn, color: "#dc2626" }} onClick={() => eliminar(u)} title="Eliminar">🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal !== null && (
        <UsuarioModal
          usuario={Object.keys(modal).length ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); traer(); }}
        />
      )}
    </div>
  );
};

export default UsersAdmin;
